import type { Community } from '@communities/community/domain';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import type { GraphQLAuditItem, GraphQLCommunity, FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { Button, ButtonType, OverlayMode, IconButton, Delete, IconButtonSize, IconSize } from '@soteria/design-system-ve';
import { useState } from 'react';

import { CommunityAuditHeroImage } from '../CommunityAuditHeroImage';
import { DeleteAuditSessionDialog } from '../DeleteAuditSessionDialog';

import { CompositionBarChart } from './CompositionBarChart';
import * as denseStyles from './InformationDenseLayout.css';
import { renderLoadingState } from './LoadingState';
import { LanguageTopicsPanel } from './components/LanguageTopicsPanel';
// import { SampleParametersPanel } from './components/SampleParametersPanel'; // Hidden - not currently used
import { SessionDetailsPanel } from './components/SessionDetailsPanel';
import * as styles from './styles';

interface CommunityAuditDetailHeaderSectionProps {
  community: Community | GraphQLCommunity | null;
  session: FlexibleAuditSession | null;
  auditItems?: GraphQLAuditItem[]; // Optional for now, will be required once BFF is implemented
  onCompleteAudit?: () => void; // New action handler
  onDeleteAuditSession?: () => void; // Delete action handler
  onViewReport?: () => void; // View report handler
  isLoading?: boolean;
}

/**
 * Header section component for audit session detail pages with gradient transition
 * Contains community name, image, and session-specific information in an information-dense layout
 * Background image is handled by the global useHeroBackground hook
 */

// eslint-disable-next-line complexity
function CommunityAuditDetailHeaderSectionComponent({
  community,
  session,
  auditItems: _auditItems,
  onCompleteAudit,
  onDeleteAuditSession,
  onViewReport,
  isLoading = false,
}: CommunityAuditDetailHeaderSectionProps) {
  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Use dataset composition from GraphQL response (calculated in BFF)
  const datasetComposition = session?.datasetComposition;

  // Delete handlers
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteSuccess = () => {
    setIsDeleteDialogOpen(false);
    onDeleteAuditSession?.();
  };

  // Render loading state with shimmer
  if (isLoading || !community || !session) {
    return renderLoadingState();
  }

  return (
    <div className={styles.heroSection}>
      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Left Column - Community Image and Name */}
        <CommunityAuditHeroImage community={community} isLoading={isLoading} />

        {/* Right Column - Session Details */}
        <div className={styles.heroContentRight}>
          {/* Session Details Panel */}
          <SessionDetailsPanel session={session} />

          {/* Three-Column Details Grid */}
          <div className={denseStyles.detailsGrid}>
            {/* Label Composition Bar Chart Panel */}
            <CompositionBarChart
              colorType="labels"
              entries={datasetComposition?.byLabel || []}
              title="Label Composition"
              totalItems={datasetComposition?.totalItems || 0}
            />

            {/* Language & Topics Composition Panel */}
            <LanguageTopicsPanel session={session} />

            {/* Sample Parameters Panel - Hidden */}
            {/* <SampleParametersPanel session={session} /> */}
          </div>

          {/* Session Info Footnote */}
          <div className={denseStyles.sessionFootnote}>
            Created {new Date(session.createdAt).toLocaleDateString()} by {session.createdBy}
            {session.completedAt && ` • Completed ${new Date(session.completedAt).toLocaleDateString()}`}
          </div>

          {/* Action Buttons Section (matching community header) - Only render if there are actions */}
          {(onCompleteAudit || onViewReport || onDeleteAuditSession) && (
            <div className={denseStyles.actionsContainer}>
              {onCompleteAudit && (
                <Button buttonType={ButtonType.Brand} disabled={session.status === 'completed'} overlayMode={OverlayMode.Solid} onClick={onCompleteAudit}>
                  COMPLETE AUDIT
                </Button>
              )}
              {onViewReport && (
                <Button buttonType={ButtonType.Secondary} disabled={session.status !== 'completed'} overlayMode={OverlayMode.Solid} onClick={onViewReport}>
                  VIEW REPORT
                </Button>
              )}
              {onDeleteAuditSession && (
                <IconButton
                  aria-label="Delete Audit Session"
                  buttonSize={IconButtonSize.XLarge}
                  buttonType={ButtonType.Danger}
                  icon={Delete}
                  iconSize={IconSize.XSmall}
                  overlayMode={OverlayMode.Solid}
                  onClick={handleDeleteClick}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Audit Session Dialog */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <DeleteAuditSessionDialog auditSession={session as any} isOpen={isDeleteDialogOpen} onClose={handleDeleteClose} onSuccess={handleDeleteSuccess} />
    </div>
  );
}

export const CommunityAuditDetailHeaderSection = withAppInsightsTracking(CommunityAuditDetailHeaderSectionComponent, 'CommunityAuditDetailHeaderSection');
